import {Text} from 'quickstart/components/content/Text'
import {ClearButton} from 'quickstart/components/controls/ClearButton'
import {Box} from 'quickstart/components/layout/Box'
import {Shape} from 'quickstart/components/layout/Shape'
import styled, {css, shouldForwardProp, system, th} from 'quickstart/system'
import * as R from 'rambdax'
import {Dialog as ReakitDialog, DialogBackdrop} from 'reakit/Dialog'

const _REAKIT_DIALOG_STATE_PROPS = [
  'animated',
  'animating',
  'baseId',
  'hide',
  'modal',
  'stopAnimation',
  'toggle',
  'visible',
]

const REAKIT_DIALOG_PROPS = [
  ..._REAKIT_DIALOG_STATE_PROPS,
  'hideOnEsc',
  'hideOnClickOutside',
  'preventBodyScroll',
  'unstable_initialFocusRef',
  'unstable_finalFocusRef',
  'unstable_orphan',
]

const REAKIT_DIALOG_BACKDROP_PROPS = _REAKIT_DIALOG_STATE_PROPS

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const REAKIT_DIALOG_DISCLOSURE_PROPS = [
  ..._REAKIT_DIALOG_STATE_PROPS,
  'disabled',
  'focusable',
]

export const Backdrop = styled(DialogBackdrop).withConfig({
  shouldForwardProp: prop =>
    shouldForwardProp(prop, 'div') ||
    REAKIT_DIALOG_BACKDROP_PROPS.includes(prop),
})<{hideOnClickOutside?: boolean | undefined}>(
  ({hideOnClickOutside}) => css`
    ${th('modals.backdrop')};
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 150ms ease-in-out;
    ${hideOnClickOutside &&
    css`
      cursor: pointer;
    `}

    /* on open dialog for animation */
    &[data-enter] {
      opacity: 1;
    }
  `,
)

export const Close = styled(ClearButton)`
  position: absolute;
  z-index: 1;
  right: 0;
  top: 0;

  padding: ${th('space.lg')};
  height: calc(25px + (${th('space.lg')} * 2));
  width: calc(25px + (${th('space.lg')} * 2));

  &:focus {
    box-shadow: none;
  }
`

export const Dialog = styled(ReakitDialog).withConfig({
  shouldForwardProp: (prop, ...rest) =>
    shouldForwardProp(prop, ...rest) || REAKIT_DIALOG_PROPS.includes(prop),
})<{size?: 'sm' | 'md' | 'lg' | 'xl' | 'auto' | undefined}>(
  ({size = 'auto', theme}) => css`
    ${th('modals.default')};
    ${th(`modals.sizes.${size}`)};
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: 20;
    opacity: 0;
    max-height: calc(100% - ${theme.toRem(20)});
    max-width: calc(100% - ${theme.toRem(20)});
    transition: opacity 250ms ease-in-out, margin-top 250ms ease-in-out;
    cursor: auto;

    &:focus {
      outline: none !important; /* important for firefox */
    }

    /* on open dialog for animation */
    &[data-enter] {
      opacity: 1;
      margin-top: 0;
    }
  `,
)

export const Content = styled(Box)`
  ${th('modals.content')};
  flex: 1;
  overflow-y: auto;
`

export const Cover = styled(Shape)`
  ${th('modals.cover')};
`

export const Title = styled(Text).attrs(
  R.mergeRight({ui: true, variant: 'modalHead'}),
)`
  flex: 0 0 auto; /* don't grow or shrink */
  ${th('modals.title')};
  ${system};
`

export const Footer = styled(Box)`
  ${th('modals.footer')};
  width: 100%;
`
